import React, { useEffect } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import Grid from '@mui/material/Grid';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
  OutlinedInput,
  Typography,
  Divider,
} from '@mui/material';
import { get } from 'lodash';
import TransactionTypeSelect from './TransactionTypeSelect';
import CustomTextInput from '../CustomTextInput';
import SingleDatePicker from './SingleDatePicker';
import dayjs from 'dayjs';
import CountrySelect from './CountrySelect';
import { transactionTypePositionCheck } from '../../utils/helper';
import { UAE_COUNTRY_CODE } from '../../constants/defaults';
import UAESubdivisionSelect from './UAESubdivisionSelect';
import { useTranslatedMessages } from '../../hooks/useTranslatedMessages';

const TransactionTypeFields = ({
  control,
  errors,
  readOnly = false,
  profileExecutionId,
  invoiceTypeCode,
  setValue,
  trigger,
}) => {
  const { eInvoiceForm } = useTranslatedMessages();
  const { transaction_type_fields: t } = eInvoiceForm;
  const frequencyOptions = t.frequency_options;
  const invoicePeriodStartDate = useWatch({ control, name: 'invoice_period_start_date' });
  const invoicePeriodEndDate = useWatch({ control, name: 'invoice_period_end_date' });
  useEffect(() => {
    if (invoicePeriodStartDate || invoicePeriodEndDate) {
      trigger(['invoice_period_start_date', 'invoice_period_end_date']);
    }
  }, [invoicePeriodStartDate, invoicePeriodEndDate, trigger]);

  const showInvoicePeriod = transactionTypePositionCheck(profileExecutionId, 4); //XXX1XXXX

  const showProfileIdTrn = transactionTypePositionCheck(profileExecutionId, 6); //XXXXX1XX

  const showDeliveryDetails =
    transactionTypePositionCheck(profileExecutionId, 7) || transactionTypePositionCheck(profileExecutionId, 8); //XXXXXX1X or XXXXXXX1

  const showBeneficiaryId = transactionTypePositionCheck(profileExecutionId, 1); //1XXXXXXX

  const handleCountryChange = (value) => {
    setValue('delivery_country_subentity', value?.code === UAE_COUNTRY_CODE ? null : '');
  };

  const deliveryCountry = useWatch({ control, name: 'delivery_country_code' });

  return (
    <>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <TransactionTypeSelect
          id="profile_execution_id"
          name="profile_execution_id"
          label={t.transaction_type_label}
          control={control}
          errors={errors}
          readOnly={readOnly}
          invoiceTypeCode={invoiceTypeCode}
          onChange={() => {}}
          helperText=""
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 4, md: 3 }}>
        <CustomTextInput
          data-testid="document-number-input"
          label={t.transaction_type_code_label}
          control={control}
          errors={errors}
          name={'profile_execution_id'}
          readOnly={true}
          helperText=""
        />
      </Grid>
      {showBeneficiaryId && (
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <CustomTextInput
            data-testid="beneficiary-id-input"
            label={t.beneficiary_id_label}
            control={control}
            errors={errors}
            name={'beneficiary_id'}
            readOnly={readOnly}
            placeholder={t.beneficiary_id_placeholder}
            helperText=""
          />
        </Grid>
      )}
      {showProfileIdTrn && (
        <Grid size={{ xs: 12, sm: 4, md: 4 }}>
          <CustomTextInput
            data-testid="document-number-input"
            label={t.principle_id_trn_label}
            control={control}
            errors={errors}
            name={'principle_id_trn'}
            readOnly={readOnly}
            disabled={false}
          />
        </Grid>
      )}
      {showInvoicePeriod && (
        <Grid container spacing={2} size={{ xs: 12, sm: 12, md: 12 }}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <SingleDatePicker
              data-testid="invoice-date-input"
              name="invoice_period_start_date"
              label={t.invoicing_period_start_label}
              control={control}
              errors={errors}
              readOnly={readOnly}
              maxDate={dayjs()}
              helperText=""
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <SingleDatePicker
              data-testid="invoice-date-input"
              name="invoice_period_end_date"
              label={t.invoicing_period_end_label}
              control={control}
              errors={errors}
              readOnly={readOnly}
              minDate={invoicePeriodStartDate ? dayjs(invoicePeriodStartDate) : dayjs()}
              helperText=""
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Controller
              name="invoice_period_description"
              control={control}
              render={({ field }) => {
                const errorMessage = get(errors, 'invoice_period_description', {})?.message;
                return (
                  <FormControl fullWidth error={!!errorMessage} variant="outlined" size="small">
                    <InputLabel id="invoice-period-description-label">{t.frequency_of_billing_label}</InputLabel>
                    <Select
                      labelId="invoice-period-description-label"
                      id="invoice-period-description"
                      data-testid="invoice-period-description"
                      value={field.value || ''}
                      onChange={field.onChange}
                      input={<OutlinedInput label={t.frequency_of_billing_label} />}
                      disabled={readOnly}
                    >
                      {frequencyOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                    {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
                  </FormControl>
                );
              }}
            />
          </Grid>
        </Grid>
      )}
      {showDeliveryDetails && (
        <>
          <Grid container spacing={2} size={{ xs: 12, sm: 12, md: 12 }}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6">{t.delivery_details_title}</Typography>
              <Divider />
            </Grid>
          </Grid>
          <Grid container spacing={2} size={{ xs: 12, sm: 12, md: 12 }}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <CustomTextInput
                data-testid="delivery-party-name-input"
                name="delivery_party_name"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={t.delivery_party_name_label}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <CustomTextInput
                data-testid="address_line-input"
                name="delivery_street_name"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={t.address_line_label}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <CustomTextInput
                data-testid="city_name-input"
                name="delivery_city_name"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={t.city_name_label}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} size={{ xs: 12, sm: 12, md: 12 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <CountrySelect
                data-testid="country_code-select"
                name="delivery_country_code"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={t.country_label}
                onChange={handleCountryChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              {deliveryCountry?.code === UAE_COUNTRY_CODE ? (
                <UAESubdivisionSelect
                  data-testid="country_subentity-select"
                  name="delivery_country_subentity"
                  control={control}
                  errors={errors}
                  readOnly={readOnly}
                />
              ) : (
                <CustomTextInput
                  data-testid="country_subentity-input"
                  name="delivery_country_subentity"
                  control={control}
                  errors={errors}
                  readOnly={readOnly}
                  label={t.country_subdivision_label}
                />
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <CustomTextInput
                data-testid="postal_zone-input"
                name="delivery_postal_zone"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={t.postal_zone_label}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <CustomTextInput
                data-testid="incoterm-input"
                label={t.incoterm_label}
                name="incoterms"
                control={control}
                errors={errors}
                readOnly={readOnly}
              />
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

export default TransactionTypeFields;
